import { Helmet } from "react-helmet-async";

const SITE_NAME = "Weston Commercial Center";
const ORIGIN = "https://westoncommercialcenter.com";
const DEFAULT_OG = "https://images.pexels.com/photos/19893586/pexels-photo-19893586.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=630&w=1200";

/**
 * Per-page SEO with title, description, canonical, Open Graph, Twitter,
 * and optional JSON-LD blocks.
 *
 *   <SEO
 *     title="..."
 *     description="..."
 *     path="/contact"
 *     image="..."
 *     jsonLd={[breadcrumbsLd, faqLd]}
 *   />
 */
export const SEO = ({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  noindex = false,
  jsonLd = [],
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Professional Office Space in Weston, Florida`;
  const url = `${ORIGIN}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {jsonLd.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

/* ------------ JSON-LD builders ------------ */

export const breadcrumbsLd = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((t, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: t.name,
    item: `${ORIGIN}${t.path}`,
  })),
});

export const faqLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const placeOfferLd = (space) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: `${space.name} for lease in Weston, FL`,
  description: space.short,
  image: space.image,
  brand: { "@type": "Organization", name: SITE_NAME },
  offers: {
    "@type": "Offer",
    priceCurrency: "USD",
    price: (space.startingRate || "").replace(/[^\d]/g, ""),
    priceSpecification: {
      "@type": "PriceSpecification",
      priceCurrency: "USD",
      price: (space.startingRate || "").replace(/[^\d]/g, ""),
      unitText: "MONTH",
    },
    availability:
      space.availability === "Available"
        ? "https://schema.org/InStock"
        : space.availability === "Limited"
        ? "https://schema.org/LimitedAvailability"
        : "https://schema.org/PreOrder",
    url: `${ORIGIN}/spaces/${space.slug}`,
  },
});

export const localBusinessLd = (brand) => ({
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  name: SITE_NAME,
  description: "Premium office space, executive suites and team workspaces for lease in Weston, Florida.",
  url: ORIGIN,
  telephone: brand.phoneRaw,
  email: brand.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: `${brand.address.street}, ${brand.address.suite}`,
    addressLocality: brand.address.city,
    addressRegion: brand.address.state,
    postalCode: brand.address.zip,
    addressCountry: "US",
  },
  areaServed: ["Weston", "Davie", "Pembroke Pines", "Sunrise", "Plantation", "Cooper City", "Broward County"],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:30",
      closes: "18:00",
    },
  ],
});

export default SEO;
